<script setup>
import { computed } from 'vue'
import Spinner from '../ui/Spinner.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'main',
  },
  size: {
    type: String,
    default: 'md',
  },
  isIcon: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  target: {
    type: String,
    default: '_blank'
  },
  customClasses: {
    // string or class-binding array; forwarded to :class which accepts both
    type: [String, Array],
  },
  to: {
    // router-link accepts both path strings and location objects
    type: [String, Object],
  },
  href: {
    type: String,
  },
  onClick: Function,
  htmlType: {
    type: String,
    default: 'button',
    validator: (v) => ['button', 'submit'].includes(v),
  },
})

const computedTypeClass = computed(() => {
  switch (props.type) {
    case 'main':
      return 'bg-main hover:bg-main-light text-surface-text-2 hover:text-white'
      case 'surface-4':
      return 'bg-surface-4 hover:bg-surface-5 text-surface-text-2 hover:text-white'
    case 'secondary':
      // Disabled: keep the outline look muted instead of the filled grey block
      // that the base disabled:bg-surface-5 would apply to an outline button.
      return props.isIcon ? 'text-surface-text-1 hover:text-white hover:text-white ring-1 ring-surface-4 hover:text-white hover:ring-surface-6 disabled:!bg-transparent disabled:!ring-surface-3 disabled:!text-surface-6' : 'text-surface-text-1 ring-1 ring-surface-4 bg-transparent hover:bg-surface-2 hover:text-white hover:ring-surface-6 focus:ring-1 focus:ring-surface-6 disabled:!bg-transparent disabled:!ring-surface-3 disabled:!text-surface-6'
    case 'link':
      return 'text-surface-text-1 hover:text-white disabled:text-surface-6'
    case 'light':
      return props.isIcon ? 'text-surface-text-1 hover:text-white hover:text-white hover:text-white disabled:text-surface-6' : 'text-surface-text-1 bg-transparent hover:bg-surface-2 hover:text-white disabled:text-surface-6'
    case 'white':
      return 'text-surface-1 bg-surface-9 hover:bg-white hover:text-black'
    case 'success':
      return 'success'
    case 'danger':
      return 'text-surface-text-1 ring-1 ring-surface-4 hover:ring-error-light hover:text-error-light focus:ring-1 focus:ring-surface-4'
    case 'red':
      return 'bg-error hover:bg-error-light text-surface-text-2 hover:text-white disabled:bg-error disabled:opacity-60 disabled:text-surface-text-2'
    case 'main-outline':
      return 'text-main ring-1 ring-main !bg-transparent hover:!bg-main-light hover:text-white hover:ring-main-light'
      case 'outline':
      return 'text-surface-text-1 ring-1 ring-surface-4 !bg-transparent hover:!bg-surface-2 hover:text-white hover:ring-surface-6'
    default: return '!border-main bg-main hover:border-main-light bg:main-light'
  }
})

const computedSizeClass = computed(() => {
  switch (props.size) {
    case 'lg':
      return props.isIcon ? 'h-10 w-10 text-base' : 'h-10 text-base'
    case 'md':
      return props.isIcon ? 'h-8 w-8  text-tiny' : 'h-8 text-tiny'
    case 'sm':
      return props.isIcon ? 'h-7 w-7 text-tiny' : 'h-7 text-tiny'
    case 'xs':
      return props.isIcon ? 'h-6 w-6 text-tiny' : 'h-6 text-tiny'
    default: return props.isIcon ? 'h-6 w-6' : 'h-8'
  }
})

// Text-only buttons (Discard, Clear, …) have no padding of their own: the grey
// disabled fill would turn them into a tight block next to the busy button.
const textOnly = computed(() => props.type === 'link' || props.type === 'light')

const computedClass = computed(() => {
  const baseClass = ['button !border-none form-button inline-flex items-center !rounded-md leading-none text-nowrap box-border font-medium hover:no-underline max-w-full truncate grow-0 shrink-0', textOnly.value ? 'disabled:bg-transparent disabled:cursor-not-allowed' : 'disabled:bg-surface-5 disabled:ring-surface-5 disabled:text-surface-7', props.isIcon ? 'p-0 justify-center' : 'py-2', (props.isIcon || props.type === 'link') ? 'border-none' : 'border !px-3']
  const result = []
  if (props.fullWidth) result.push('!w-full justify-center')
  result.push(baseClass.join(' '), computedSizeClass.value, computedTypeClass.value)
  if (props.customClasses) return props.isLoading ? [props.customClasses, 'is-loading relative cursor-progress'] : props.customClasses
  if (!props.isLoading) return result.join(' ')
  // Busy: the label stays in the box (invisible) so the width does not jump,
  // the spinner sits over it, and the button keeps its own colours (no grey
  // disabled look from `:disabled="saving"`, no hover change under the cursor).
  return [...result.join(' ').split(' ').filter((c) => c && !c.startsWith('disabled:') && (!c.startsWith('hover:') || c === 'hover:no-underline')), 'is-loading relative cursor-progress'].join(' ')
})
</script>

<template>
  <button :ref="props.ref" v-if="!to && !href" :type="htmlType" @click="onClick" :class="computedClass" :disabled="disabled" :aria-busy="isLoading || undefined">
    <template v-if="!isLoading">
      {{ label }}
      <slot />
    </template>
    <template v-else>
      <span class="invisible contents" aria-hidden="true">{{ label }}<slot /></span>
      <Spinner classes="absolute inset-0" />
    </template>
  </button>
  <router-link v-else-if="to" :to="to" :class="computedClass" :disabled="disabled" :aria-busy="isLoading || undefined">
    <template v-if="!isLoading">
      {{ label }}
      <slot />
    </template>
    <template v-else>
      <span class="invisible contents" aria-hidden="true">{{ label }}<slot /></span>
      <Spinner classes="absolute inset-0" />
    </template>
  </router-link>
  <a v-else-if="href" :target="props.target" :class="computedClass" :disabled="disabled" :href="href" :aria-busy="isLoading || undefined">
    <template v-if="!isLoading">
      {{ label }}
      <slot />
    </template>
    <template v-else>
      <span class="invisible contents" aria-hidden="true">{{ label }}<slot /></span>
      <Spinner classes="absolute inset-0" />
    </template>
  </a>
</template>

<style>
@layer tailwind {
  button[disabled].bg-main:not(.is-loading) {
    @apply !border-surface-4 text-surface-6
  }
}
</style>
